import { useCallback, useEffect, useState } from 'react';
import { Button, Dialog, Icon, useErrorDialog, useToast } from '@dayon/design-system';
import type { Column } from '@dayon/design-system';

import { api, ApiRequestError } from '../../lib/api.ts';
import type { SavedViewScreen } from './SavedViewsDialog.tsx';

/**
 * Choose which columns a table shows — per admin, per screen.
 *
 * ── WHAT WAS ACTUALLY MISSING, WHICH WAS LESS THAN THE SCREEN CLAIMED ─────
 *
 * The Columns dialog said "the table declares its columns inline, so there is
 * no list of them to offer you, and no way to name the ones you turned off".
 * Half of that was never true: `Column` has carried a stable `key` and a human
 * `header` since the design system shipped, so the list and the names were both
 * there. What was missing was somewhere to keep the answer, which the saved-view
 * work built and `770_admin_column_choice.sql` completes.
 *
 * ── IT SENDS THE HIDDEN SET ───────────────────────────────────────────────
 *
 * Not the visible one, and that is the decision the store is built around: a
 * column added to a screen next quarter appears for everybody by default,
 * rather than staying invisible to every admin who ever opened this dialog.
 *
 * ── A REQUIRED COLUMN CANNOT BE TURNED OFF ────────────────────────────────
 *
 * Enforced here, because this is where the column set lives — the server holds
 * keys and knows nothing about what they mean, and says so in the route. A
 * required column's checkbox is rendered, disabled, with the reason beside it
 * rather than omitted: a control that vanishes teaches an operator the console
 * is broken, one that explains itself teaches them the rule.
 */
export function ColumnPickerDialog<Row>({
  open,
  screen,
  screenLabel,
  columns,
  hidden,
  onClose,
  onChanged,
}: {
  open: boolean;
  screen: SavedViewScreen;
  screenLabel: string;
  /** The screen's full column set, in the order the table declares them. */
  columns: Column<Row>[];
  /** The keys currently turned off, as the screen has them. */
  hidden: string[];
  onClose: () => void;
  onChanged: (hidden: string[]) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [draft, setDraft] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  /* Re-seeded each time it opens, so a cancelled edit leaves nothing behind. */
  useEffect(() => {
    if (open) setDraft(hidden);
  }, [open, hidden]);

  const isHidden = (key: string) => draft.includes(key);
  const visibleCount = columns.length - draft.length;

  const toggle = (key: string, required: boolean) => {
    if (required) return;
    setDraft((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
  };

  const save = useCallback(async () => {
    setBusy(true);
    try {
      await api.post<{ hidden: string[] }>('/api/views/columns', { screen, hidden: draft });
      onChanged(draft);
      toast(
        draft.length === 0
          ? `Every column is showing on ${screenLabel}.`
          : `${draft.length} ${draft.length === 1 ? 'column is' : 'columns are'} hidden on ${screenLabel}. This is yours alone — nobody else's table changes.`,
      );
      onClose();
    } catch (error: unknown) {
      showError({
        title: 'The column choice was not saved',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  }, [draft, screen, screenLabel, onChanged, onClose, showError, toast]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Columns — ${screenLabel}`}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={busy} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="notice notice-quiet">
          <Icon name="columns" />
          <div>
            This is <strong>your</strong> table and nobody else&rsquo;s — a column you turn off is
            hidden for you, on this screen, on every device you sign in from. It changes what you
            see, never what the platform holds, and never what an export contains.
            <br />
            Below 900px the secondary columns drop out on their own whatever you choose here, so a
            narrow window shows fewer than this list does.
          </div>
        </div>

        <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {columns.map((c) => {
            const required = c.required === true;
            return (
              <li key={c.key}>
                <label className="row" style={{ gap: 'var(--space-2)' }}>
                  <input
                    type="checkbox"
                    checked={!isHidden(c.key)}
                    disabled={required}
                    onChange={() => toggle(c.key, required)}
                  />
                  <span>
                    {c.header}
                    {required ? (
                      <span
                        className="text-subtle"
                        style={{ display: 'block', fontSize: '11px' }}
                      >
                        always shown — without it a row says nothing about which record it is
                      </span>
                    ) : null}
                    {!required && c.secondary ? (
                      <span
                        className="text-subtle"
                        style={{ display: 'block', fontSize: '11px' }}
                      >
                        drops out on a narrow window anyway
                      </span>
                    ) : null}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            {visibleCount} of {columns.length} columns showing.{' '}
            {draft.length > 0 ? (
              <Button small onClick={() => setDraft([])}>
                Show them all again
              </Button>
            ) : (
              'Nothing is hidden.'
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
